/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import cx from 'classnames';
import { Box, useSafeContext } from '../../utils/index.js';
import { Toast } from './Toast.js';
// ----------------------------------------------------------------------------
export const useToaster = () => {
    const dispatch = useSafeContext(ToasterDispatchContext);
    return React.useMemo(() => {
        const showToast = (category) => (content, options) => {
            const id = nextId();
            dispatch({
                type: 'add',
                toast: { ...options, id, content, category },
            });
            return { close: () => dispatch({ type: 'remove', id }) };
        };
        return {
            positive: showToast('positive'),
            informational: showToast('informational'),
            negative: showToast('negative'),
            warning: showToast('warning'),
            closeAll: () => {
                dispatch({ type: 'close-all' });
            },
            setSettings: (settings) => {
                dispatch({ type: 'settings', settings });
            },
        };
    }, [dispatch]);
};
// ----------------------------------------------------------------------------
export const Toaster = () => {
    const { toasts, settings } = useSafeContext(ToasterStateContext);
    return (React.createElement(Box, { className: cx(`iui-toast-wrapper`, `iui-placement-${settings.placement}`) }, toasts.map((toastProps) => {
        return React.createElement(Toast, { key: toastProps.id, ...toastProps });
    })));
};
// ----------------------------------------------------------------------------
export const ToastProvider = ({ children }) => {
    const [toasterState, dispatch] = React.useReducer(toastReducer, {
        toasts: [],
        settings: {
            order: 'auto',
            placement: 'top',
        },
    });
    return (React.createElement(ToasterDispatchContext.Provider, { value: dispatch },
        React.createElement(ToasterStateContext.Provider, { value: toasterState }, children)));
};
const toastReducer = (state, action) => {
    if (action.type === 'add') {
        let order = state.settings.order;
        if (order === 'auto') {
            order = state.settings.placement.startsWith('top')
                ? 'descending'
                : 'ascending';
        }
        return {
            ...state,
            toasts: [
                ...(order === 'ascending' ? state.toasts : []),
                action.toast,
                ...(order === 'descending' ? state.toasts : []),
            ],
        };
    }
    if (action.type === 'remove') {
        return {
            ...state,
            toasts: state.toasts.filter((toast) => toast.id !== action.id),
        };
    }
    if (action.type === 'close-all') {
        return {
            ...state,
            toasts: state.toasts.map((toast) => ({ ...toast, isVisible: false })),
        };
    }
    if (action.type === 'settings') {
        return { ...state, settings: { ...state.settings, ...action.settings } };
    }
    return state;
};
// ----------------------------------------------------------------------------
export const ToasterStateContext = React.createContext(undefined);
ToasterStateContext.displayName = 'ToasterStateContext';
// ----------------------------------------------------------------------------
const ToasterDispatchContext = React.createContext(undefined);
ToasterDispatchContext.displayName = 'ToasterDispatchContext';
// ----------------------------------------------------------------------------
const nextId = (() => {
    let count = 0;
    return () => ++count;
})();
